import {
  ApolloClient,
  ApolloLink,
  type ApolloMiddleware,
  createHttpLink,
  type DefaultContext,
  from,
  InMemoryCache,
  Observable,
  type ObservableSubscription,
  type Operation,
} from '~/lazy_apollo/client';
import { setContext } from '~/lazy_apollo/client/link/context';
import operationStoreClient from './operationStoreClient';
import possibleTypes from '../libs/gql/schema/possibleTypes.json';
import { initApolloLogsWithTestData } from './testDataLogs';
import { captureSentryMessage } from './sentry';

export const initApollo = ({ ssr, url, operationStoreEnabled, restaurantId }: { ssr: boolean, url: string, operationStoreEnabled: boolean, restaurantId?: number }) => {
  // Attempt to connect to shared client initialized by another Popmenu instance
  if (typeof window !== 'undefined' && window.POPMENU_CLIENT) {
    console.log('[POPMENU] Connecting to shared client...');
    return window.POPMENU_CLIENT;
  }

  // https://www.apollographql.com/docs/react/data/fragments#generating-possibletypes-automatically
  const cache = new InMemoryCache({ possibleTypes });
  if (typeof window !== 'undefined' && window.POPMENU_APOLLO_STATE) {
    cache.restore(window.POPMENU_APOLLO_STATE);
  }

  // Prevent performing http requests when prerendering. Every query is expected to be preloaded
  // into the cache, so a request reaching the link is an error.
  if (ssr) {
    return new ApolloClient({
      cache,
      link: new ApolloLink(() => new Observable((observer) => {
        observer.error(new Error('[POPMENU] SSR Apollo client cannot execute network requests'));
      })),
      ssrMode: true,
    });
  }

  // Add operation store and http links
  const links: ApolloMiddleware[] = [];
  if (operationStoreEnabled && operationStoreClient) {
    links.push(operationStoreClient.apolloLink);
  }

  const CLIENT_UUID = crypto.randomUUID();

  const requesterLink = setContext((_, { headers = {} }: { headers?: Record<string, string>}) => ({
    headers: {
      ...headers,
      'x-popmenu-client-uuid': CLIENT_UUID,
    },
  }));
  links.push(requesterLink);

  if (process.env.LOG_TEST_DATA_TO_BROWSER_CONSOLE) {
    initApolloLogsWithTestData(links);
  }

  type Context = DefaultContext & {
    fetchOptions: {
      method: 'GET' | 'POST';
    };
    headers: Record<string, string>;
  };

  const setHTTPMethod = (operation: Operation, method: 'GET' | 'POST') => {
    operation.setContext((prevContext: Context) => ({
      fetchOptions: {
        ...prevContext.fetchOptions,
        method,
      },
    }));
  };

  const customFetch: typeof fetch = async (input, init) => {
    if (init?.method === 'GET') {
      // Apollo Client never provides a `Request` as input, only `string` or maybe `URL`
      const fetchUrl = new URL(input as string);
      const operationName = fetchUrl.searchParams.get('operationName') || '';
      const cacheKey = window.popmenuGqlCacheKeys?.[operationName];
      if (!cacheKey) {
        captureSentryMessage(`[POPMENU] Missing cache key for operation ${operationName}`);
        return fetch(fetchUrl, init);
      }

      fetchUrl.searchParams.set('httpCacheKey', cacheKey);
      const query = fetchUrl.searchParams.get('query');
      if (query) {
        // We could use graphql.stripIgnoredCharacters, but this is faster, has most of the benefit,
        // and avoids increased bundle size.
        fetchUrl.searchParams.set('query', query.replace(/\s+/g, ' '));
      }
      return fetch(fetchUrl, init);
    }
    return fetch(input, init);
  };

  if (window.popmenuGqlCacheKeys) {
    const useGetLink = new ApolloLink((operation, forward) => {
      const { operationName, variables } = operation;

      // Will be removed in https://app.shortcut.com/popmenu/story/102146
      const isOrderingEventMenuSectionQuery =
        operationName === 'menuSection' &&
        variables.orderingEventId != null &&
        variables.orderingEventId !== -1;

      if (!window.popmenuGqlCacheKeys?.[operationName] || isOrderingEventMenuSectionQuery) {
        return forward(operation);
      }
      // Try GET request first. This will enable cloudflare and browser caching.
      setHTTPMethod(operation, 'GET');

      return new Observable((observer) => {
        let currentSubscription: ObservableSubscription | null = null;
        currentSubscription = forward(operation).subscribe({
          complete: () => observer.complete(),
          error: (error) => {
            captureSentryMessage(`[POPMENU] Failed GET request for GQL query ${operationName}: ${error}. Restaurant ID: ${restaurantId}`);
            // Prevent further attempts to use Cached GQL queries.
            window.popmenuGqlCacheKeys = undefined;
            currentSubscription?.unsubscribe();
            currentSubscription = null;

            // Try again with POST request to reach origin server in case
            // of cloudflare-related errors.
            setHTTPMethod(operation, 'POST');
            currentSubscription = forward(operation).subscribe(observer);
          },
          next: data => observer.next(data),
        });
        return () => {
          currentSubscription?.unsubscribe();
          currentSubscription = null;
        };
      });
    });

    links.push(useGetLink);
  }

  links.push(
    createHttpLink({
      credentials: 'include',
      fetch: window.popmenuGqlCacheKeys && customFetch,
      uri: url,
    }),
  );
  const httpLink = from(links);

  // Initialize client
  console.log('[POPMENU] Init Popmenu Apollo Client');
  const client = new ApolloClient({
    cache,
    link: httpLink,
    ssrMode: false,
  });

  if (typeof window !== 'undefined') {
    window.POPMENU_CLIENT = client;
  }
  return client;
};
